import { outcomeOf } from '../catalog/pipeline/outcome';

/**
 * The deployments snapshot as `github-insights` serves it (`/deployments`), read
 * by the home page's Recent deployments card and by the Deployments page.
 *
 * One module for both, and one verdict on whether a deploy failed — the same one
 * its outcome pill shows.
 */

export type DeploymentEnvironment = 'production' | 'qm' | 'test';

export type DeploymentRecord = {
  slug: string;
  org: string;
  workflow: string;
  htmlUrl: string;
  branch: string | null;
  conclusion: string | null;
  status: string | null;
  createdAt: string;
  durationSec: number | null;
  /**
   * Where it went (DX-304). Optional because a snapshot collected before the
   * backend knew environments carries none; such a deploy has no environment
   * rather than a guessed one.
   */
  environment?: DeploymentEnvironment;
  /** True when the workflow names no environment and production is inferred. */
  environmentDerived?: boolean;
  /**
   * True for a customer project's row (DX-681), absent for every other: the
   * backend marks the repositories under `githubInsights.customerProjects`.
   */
  customerProject?: boolean;
};

export type DeploymentsSnapshot = {
  generatedAt: string | null;
  windowDays: number;
  totals: { deployments: number; repos: number; failed: number };
  deployments: DeploymentRecord[];
};

/**
 * Whether a deployment is one that failed: exactly the runs the portal marks red
 * wherever it shows an outcome — a failure, a time-out, a failure to start.
 *
 * Read off `outcomeOf` rather than written out again, so a row's pill, the
 * page's "Failed" filter, its change failure rate and the card's "· N failed"
 * are one verdict. `neutral`, `stale`, a completed run without a conclusion and
 * a conclusion GitHub introduces tomorrow are shown grey and are not failures;
 * counting them once put a failure in the rate above a grey row (DX-304 review).
 *
 * It has to agree with `isFailedDeployment` in the github-insights backend,
 * which counts the card's figure. The frontend cannot import from a backend
 * plugin, so the two are kept identical and each says so. They disagreed once
 * before — a cancelled deployment counted as failed above a row that showed it
 * as neither — and each side had a test asserting its own reading.
 */
export function isFailedDeployment(deployment: DeploymentRecord): boolean {
  return outcomeOf(deployment).kind === 'failure';
}
